import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAnnotations,
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
    colorBlack,
    colorWhite,
} from 'src/app/shared/all.constants';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    ACCOUNTING_RIGHTS_BREAKDOWN_GRANTOR_CHART,
    ACCOUNTING_RIGHTS_DISTRIB_ORIGIN_FINAN_CHART,
    ACCOUNTING_RIGHTS_EVOL_DEBTS_CHART,
    ACCOUNTING_RIGHTS_EVOL_ORIGIN_NETBOOK_CHART,
    ACCOUNTING_RIGHTS_GRANTOR_EVOL_CHART,
} from '../../assets/keys';

const KEYS = [
    ACCOUNTING_RIGHTS_EVOL_ORIGIN_NETBOOK_CHART,
    ACCOUNTING_RIGHTS_DISTRIB_ORIGIN_FINAN_CHART,
    ACCOUNTING_RIGHTS_GRANTOR_EVOL_CHART,
    ACCOUNTING_RIGHTS_BREAKDOWN_GRANTOR_CHART,
    ACCOUNTING_RIGHTS_EVOL_DEBTS_CHART,
];

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=461
    rateNetBook?: number;
    //kpi_id=462
    rateNetBookCommu?: number;
    //kpi_id=569
    totalNetBook?: number;
    error?: boolean;
}
interface DistribOrgFinanChartData {
    organizationId?: string;
    year?: string;
    communitiesTrdPart?: number;
    enedis?: number;
    error?: boolean;
}

interface GrantorRightsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    grantorRights?: number;
    error?: boolean;
}

interface BreakDownChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    netValueWorks?: number;
    amortiFinan?: number;
    unamortizedValue?: number;
    grantorRights?: number;
    error?: boolean;
}

interface DebtsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    depreciationGrantor?: number;
    provisionRenewal?: number;
    undepreciatedValue?: number;
    resulting?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-accounting-rights',
    templateUrl: './accounting-rights.component.html',
    styleUrls: ['./accounting-rights.component.scss'],
})
export class AccountingRightsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.grantorRights.texts;

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_RIGHTS_EVOL_ORIGIN_NETBOOK_CHART),
    ) as Signal<ChartData[]>;

    public chartError: boolean = true;
    public chartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null }),
    };
    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolutionOriginFinancingChart.rateNetBookAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolutionOriginFinancingChart.yaxisTitle,
            },
        },
        {
            seriesName: this.texts.evolutionOriginFinancingChart.rateNetBookAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolutionOriginFinancingChart.totalNetBookAxisTitle,
            min: 0,
            opposite: true,
        },
    ];

    public plotOptions: ApexPlotOptions = {
        bar: {
            horizontal: false,
            columnWidth: '30%',
        },
    };

    public distribSourceFinanChartTitle = this.texts.distribSourcesFinanChart.title;
    public distribSourceFinanChartError: boolean = true;
    public distribSourceFinanChartSeries: number[] = [];
    public distribSourceFinanLabels: string[] = [];
    public distribSourceFinanColors: string[] = [colorAec3Green, colorAec1BlueLight];
    public distribSourceFinanDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: (val: number) =>
            convertNumber(val, this.texts.distribSourcesFinanChart.precision) + '%',
    };
    public distribSourceFinanChartData: Signal<DistribOrgFinanChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ACCOUNTING_RIGHTS_DISTRIB_ORIGIN_FINAN_CHART),
        ) as Signal<DistribOrgFinanChartData[]>;

    public grChartError: boolean = true;
    public grChartSeries: ApexAxisChartSeries = [];
    public grDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null }),
    };
    public grXaxisCategories: ApexXAxis['categories'];
    public grYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolGrantorRightsChart.grantorRightsAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolGrantorRightsChart.yaxisTitle,
            },
        },
    ];
    public grantorRightsChartData: Signal<GrantorRightsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_RIGHTS_GRANTOR_EVOL_CHART),
    ) as Signal<GrantorRightsChartData[]>;
    public evolGrantorRightsGrid: ApexGrid = { padding: { right: 50 } };

    public breakChartError: boolean = true;
    public breakChartSeries: ApexAxisChartSeries = [];
    public breakDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        background: {
            enabled: true,
            foreColor: colorWhite,
            borderColor: colorWhite,
        },
        style: {
            colors: [colorAec2BlueDark, colorAec4Orange, colorAec3Green, colorAec6Gray],
            fontSize: '11px',
        },
    };
    public breakXaxisCategories: ApexXAxis['categories'];
    public breakYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
        },
    ];
    public breakPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            columnWidth: '50%',
            barHeight: '70%',
            // dataLabels: {
            //     position: 'center',
            // },
        },
    };
    public breackXaxisLabels: ApexXAxis['labels'] = {
        formatter: (value: any) => {
            if (typeof value === 'number') {
                return convertNumber(value, null, false);
            }
            return value;
        },
    };
    public breakDownChartData: Signal<BreakDownChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_RIGHTS_BREAKDOWN_GRANTOR_CHART),
    ) as Signal<BreakDownChartData[]>;

    public debtsChartError: boolean = true;
    public debtsChartSeries: ApexAxisChartSeries = [];
    public debtsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public debtsXaxisCategories: ApexXAxis['categories'];
    public debtsYaxis!: ApexYAxis[];

    public debtsChartData: Signal<DebtsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_RIGHTS_EVOL_DEBTS_CHART),
    ) as Signal<DebtsChartData[]>;
    public debtsGrid: ApexGrid = { padding: { right: 50 } };

    public evolutionOriginFinancingCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            this.chartError = this.checkForError(dataList, [
                'xYear',
                'rateNetBook',
                'rateNetBookCommu',
                'totalNetBook',
            ]);
            if (!this.chartError) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                this.chartSeries = [
                    {
                        name: this.texts.evolutionOriginFinancingChart.rateNetBookAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.rateNetBook!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.evolutionOriginFinancingChart.rateNetBookCommuAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.rateNetBookCommu!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolutionOriginFinancingChart.totalNetBookAxisTitle,
                        type: 'line',
                        data: dataList.map((data) => data.totalNetBook!),
                        color: colorAec7RedLight,
                    },
                ];
                this.dataLabels.enabledOnSeries = [0, 1, 2];
            }

            const distribSourceFinanDataList = this.distribSourceFinanChartData();
            this.distribSourceFinanChartError = this.checkForError(distribSourceFinanDataList, [
                'communitiesTrdPart',
                'enedis',
            ]);
            if (!this.distribSourceFinanChartError) {
                this.distribSourceFinanLabels = [
                    this.texts.distribSourcesFinanChart.communitiesTrdPartTitle,
                    this.texts.distribSourcesFinanChart.enedisTitle,
                ];
                this.distribSourceFinanChartSeries = [
                    distribSourceFinanDataList[0].communitiesTrdPart || 0,
                    distribSourceFinanDataList[0].enedis || 0,
                ];
                this.distribSourceFinanDataLabels.enabledOnSeries = [0, 1];
            }

            const grDataList = this.grantorRightsChartData();
            this.grChartError = this.checkForError(grDataList, ['xYear', 'grantorRights']);
            if (!this.grChartError) {
                this.grXaxisCategories = this.getLastValues(grDataList, 5).map(
                    (data) => data.xYear,
                );

                this.grChartSeries = [
                    {
                        name: this.texts.evolGrantorRightsChart.grantorRightsAxisTitle,
                        type: 'column',
                        data: this.getLastValues(grDataList, 5).map((data) => data.grantorRights!),
                        color: colorAec6Gray,
                    },
                ];
                this.grDataLabels.enabledOnSeries = [0];
            }

            const breakDataList = this.breakDownChartData();
            this.breakChartError = this.checkForError(breakDataList, [
                'xYear',
                'netValueWorks',
                'amortiFinan',
                'unamortizedValue',
                'grantorRights',
            ]);
            if (!this.breakChartError) {
                // display only most recent year as grouped bar chart
                // todo : back must send only data for the current year !
                const breakDownChartData = breakDataList.reduce(
                    (max, item) => (item.xYear! > max.xYear! ? item : max),
                    breakDataList[0],
                );

                if (Object.keys(breakDownChartData).length) {
                    this.breakXaxisCategories = [breakDownChartData.xYear!.toString()];
                    this.breakChartSeries = [
                        {
                            name: this.texts.breakdownGrChart.netValueWorksAxisTitle,
                            type: 'bar',
                            data: [breakDownChartData.netValueWorks!],
                            color: colorAec2BlueDark,
                        },
                        {
                            name: this.texts.breakdownGrChart.unamortizedValueAxisTitle,
                            type: 'bar',
                            data: [breakDownChartData.unamortizedValue!],
                            color: colorAec4Orange,
                        },
                        {
                            name: this.texts.breakdownGrChart.amortiFinanAxisTitle,
                            type: 'bar',
                            data: [breakDownChartData.amortiFinan!],
                            color: colorAec3Green,
                        },
                        {
                            name: this.texts.breakdownGrChart.grantorRightsAxisTitle,
                            type: 'bar',
                            data: [breakDownChartData.grantorRights!],
                            color: colorAec6Gray,
                        },
                    ];

                    this.breakDataLabels.enabledOnSeries = [0, 1, 2, 3];
                }
            }

            const debtsDataList = this.debtsChartData();
            this.debtsChartError = this.checkForError(debtsDataList, [
                'xYear',
                'depreciationGrantor',
                'provisionRenewal',
                'undepreciatedValue',
                'resulting',
            ]);
            if (!this.debtsChartError) {
                this.debtsXaxisCategories = this.getLastValues(
                    debtsDataList,
                    this.texts.evolDebtsChart.maxHistoryYears,
                ).map((data) => data.xYear);

                const baseData = this.getLastValues(
                    debtsDataList,
                    this.texts.evolDebtsChart.maxHistoryYears,
                );

                this.debtsChartSeries = [
                    {
                        name: this.texts.evolDebtsChart.series[0].name,
                        type: 'column',
                        data: baseData.map((data) => data.depreciationGrantor!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolDebtsChart.series[1].name,
                        type: 'column',
                        data: baseData.map((data) => data.provisionRenewal!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolDebtsChart.series[2].name,
                        type: 'column',
                        data: baseData.map((data) => data.undepreciatedValue!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolDebtsChart.series[3].name,
                        type: 'line',
                        data: baseData.map((data) => data.resulting!),
                        color: colorAec8RedDark,
                    },
                ];
                this.debtsDataLabels.enabledOnSeries = [3];

                let minValue = baseData.reduce(
                    (min, d) =>
                        Math.min(
                            min,
                            Math.min(0, d.depreciationGrantor ?? 0) +
                                Math.min(0, d.provisionRenewal ?? 0) +
                                Math.min(0, d.undepreciatedValue ?? 0),
                        ),
                    0,
                );
                let maxValue = baseData.reduce(
                    (max, d) =>
                        Math.max(
                            max,
                            Math.max(0, d.depreciationGrantor ?? 0) +
                                Math.max(0, d.provisionRenewal ?? 0) +
                                Math.max(0, d.undepreciatedValue ?? 0),
                        ),
                    0,
                );
                if (minValue < 0 && maxValue > 0) {
                    minValue = Math.min(minValue, -maxValue);
                    maxValue = Math.max(-minValue, maxValue);
                } else if (minValue > 0) {
                    minValue = 0;
                } else {
                    maxValue = 0;
                }
                const resultingMin = baseData.reduce((m, d) => Math.min(m, d.resulting!), Infinity);
                const resultingMax = baseData.reduce(
                    (m, d) => Math.max(m, d.resulting!),
                    -Infinity,
                );

                this.debtsYaxis = [
                    {
                        seriesName: [
                            this.texts.evolDebtsChart.series[0].name,
                            this.texts.evolDebtsChart.series[1].name,
                            this.texts.evolDebtsChart.series[2].name,
                        ],
                        forceNiceScale: true,
                        tickAmount: 4,
                        decimalsInFloat: 0,
                        min: Math.floor(minValue) - (4 - Math.abs(Math.floor(minValue) % 4)),
                        max: Math.ceil(maxValue) + (4 - Math.abs(Math.ceil(maxValue) % 4)),
                        title: {
                            text: this.texts.evolDebtsChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [this.texts.evolDebtsChart.series[3].name],
                        opposite: true,
                        forceNiceScale: true,
                        min: Math.floor(resultingMin) - Math.abs(Math.floor(resultingMin) % 4),
                        max: Math.ceil(resultingMax) + Math.abs(Math.ceil(resultingMax) % 4),
                        decimalsInFloat: 0,
                        tickAmount: 4,
                        title: {
                            text: this.texts.evolDebtsChart.yaxisTitleRight,
                        },
                        labels: {
                            offsetX: 40,
                        },
                    },
                ];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(([currentOrganization, currentYear]) => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.evolutionOriginFinancingCustomTooltip = this.setChartCustomTooltip(
            this.texts.evolutionOriginFinancingChart.precision,
            this.texts.evolutionOriginFinancingChart.units,
        );
    }

    public setChartCustomTooltip(
        precision: number | null = null,
        units: string[] = [],
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const year = w.globals.categoryLabels[dataPointIndex];
            const hiddenSeries = new Set(w.globals.collapsedSeriesIndices);

            let tooltipHtml = `<div class="apexcharts-tooltip-title">${year}</div>`;

            (
                w.globals.initialSeries as Array<{ name: string; color?: string; data: any[] }>
            ).forEach((serie, idx) => {
                if (hiddenSeries.has(idx)) return;

                const serieName = serie.name;
                const serieColor = serie.color || colorBlack;
                const dataPoint = serie.data[dataPointIndex];

                if (dataPoint !== undefined) {
                    const value =
                        precision !== null ? Number(dataPoint).toFixed(precision) : dataPoint;
                    const unit = units[idx] || '';

                    tooltipHtml += `
                        <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ${idx}; display: flex; align-items: center;">
                            <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}; margin-right: 8px;"></span>
                            <div class="apexcharts-tooltip-text">
                                <div class="apexcharts-tooltip-y-group">
                                    <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                                    <span class="apexcharts-tooltip-text-y-value">${convertNumber(value, precision, false)} ${unit}</span>
                                </div>
                            </div>
                        </div>
                    `;
                }
            });

            return tooltipHtml;
        };
    }
    /*
    private computeStackMinMax(data: DebtsChartData[], keys: (keyof DebtsChartData)[]): { min: number; max: number } {
        let globalMax = Number.NEGATIVE_INFINITY;
        let globalMin = Number.POSITIVE_INFINITY;

        data.forEach((d: DebtsChartData) => {
            const values: any[] = [];
            keys.forEach((k: keyof DebtsChartData)=> values.push(d[k]??0));

            const positiveTotal = values.filter((v) => v > 0).reduce((a, b) => a + b, 0);

            const negativeTotal = values.filter((v) => v < 0).reduce((a, b) => a + b, 0);

            globalMax = Math.max(globalMax, positiveTotal);
            globalMin = Math.min(globalMin, negativeTotal);
        });

        return {
            min: Math.floor(globalMin),
            max: Math.ceil(globalMax),
        };
    }
        */
}
